import { Box, Button, Divider, Drawer, Grid, Typography, TextField, InputAdornment } from "@mui/material";
import { useEffect, useState, useRef } from "react";
import { useTicketStore } from "../../../../hooks/useTicketStore";
import CustomTable from "../../../../components/common/CustomTable/CustomTable";
import { ApiStatus } from "../../../../types/api/status";
import { Ticket } from "../../../../types/slices/ticketType";
import SearchIcon from '@mui/icons-material/Search';
import printJS from "print-js";
import { SaleDetailDrawer } from "../../../Sales/components/drawers/SaleDetailDrawer";
import { Supplier } from "../../../../types/slices/supplierType";
import { useSupplierStore } from "../../../../hooks/useSupplierStore";

interface Props {
    drawerOpen: boolean;
    handleCloseDrawer: () => void;
}

const columns = [
    { type: "checkbox", field: "", label: "", align: "center" },
    { type: "text", field: "date", label: "Fecha", align: "center" },
    { type: "text", field: "folio", label: "Folio", align: "center" },
    { type: "text", field: "type_document", label: "Tipo", align: "center" },
    { type: "text", field: "rut_customer", label: "RUT", align: "center" },
    { type: "text", field: "name_customer", label: "Cliente", align: "center" },
    { type: "text", field: "status_route_name", label: "Estado", align: "center" },
    { type: "detail", field: "", label: "", align: "center" },
];

export const DrawerInvoiceRoute: React.FC<Props> = (props) => {
    const { drawerOpen, handleCloseDrawer } = props;
    const { status, getRoutesInvoice, deleteTicket, n_routeInvoice, routesInvoice, routeSelected, setSelectedRoute, updateRoute, updateStatusRouteTicket, getRoutes } = useTicketStore();
    const { status: statusSupplier, supppliers, getSuppliers } = useSupplierStore()

    const [selectedRows, setSelectedRows] = useState<Ticket[]>([]);
    const [allSelected, setAllSelected] = useState<boolean>(false);
    const [processing, setProcessing] = useState<boolean>(false);

    const [search, setSearch] = useState<string>('');
    const [page, setPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(25);
    const [focusedRow, setFocusedRow] = useState<number | null>(null);

    const searchRef = useRef<HTMLInputElement | null>(null);
    const drawerRef = useRef<HTMLDivElement | null>(null);

    const [showDrawer, setShowDrawer] = useState<string>('');
    const [rowSelected, setRowSelected] = useState<any>('');
    const [loading, setLoading] = useState(false);
    const [showModalConfirm, setShowModalConfirm] = useState<boolean>(false);
    const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null)

    useEffect(() => {
        if (drawerOpen) {
            getSuppliers({ idroute: routeSelected.id })
        }
    }, [drawerOpen])

    useEffect(() => {
        if (drawerOpen) {
            if (search === '') {
                getRoutesInvoiceAPI();
            } else {
                const timer = setTimeout(() => getRoutesInvoiceAPI(), 500);
                return () => clearTimeout(timer);
            }
        }
    }, [drawerOpen, search, page, perPage, selectedSupplier]);


    const getRoutesInvoiceAPI = async () => {
        const payload: any = { page, perPage, idcustomer: selectedSupplier?.id };
        if (search !== '') payload.search = search;
        payload.status_invoice = 2;
        await getRoutesInvoice(routeSelected.id, payload);
        const response = await getRoutes({ id: routeSelected.id });
        setSelectedRoute(response[0]);
    };

    const onSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value);
    };

    const handleChangePage = (event: any, newPage: any) => {
        setPage(newPage + 1);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPerPage(+event.target.value);
        setPage(1);
    };

    const selectAllRows = () => {
        if (allSelected) {
            setSelectedRows([]);
            setAllSelected(false);
        } else {
            setSelectedRows(routesInvoice);
            setAllSelected(true);
        }
    };

    const isSelected = (row: Ticket) => selectedRows.some(selectedRow => selectedRow.id === row.id);

    const handleSelectItem = (row: Ticket) => {
        setSelectedRows((prev) =>
            prev.some(selectedRow => selectedRow.id === row.id)
                ? prev.filter(selectedRow => selectedRow.id !== row.id)
                : [...prev, row]
        );
    };

    /** Manejo de navegación con teclas */
    const handleKeyDown = (event: KeyboardEvent) => {
        if (!drawerOpen || routesInvoice.length === 0) return;

        if (event.key === "ArrowDown") {
            event.preventDefault();
            setFocusedRow((prev) => (prev === null || prev >= routesInvoice.length - 1 ? 0 : prev + 1));
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setFocusedRow((prev) => (prev === null || prev <= 0 ? routesInvoice.length - 1 : prev - 1));
        } else if (event.key === "Enter") {
            event.preventDefault();
            if (focusedRow !== null) {
                handleSelectItem(routesInvoice[focusedRow]);
            }
        } else if (event.ctrlKey && event.key === "a") {
            event.preventDefault();
            if (selectedRows.length === routesInvoice.length) {
                setSelectedRows([]);
                setAllSelected(false);
            } else {
                setSelectedRows(routesInvoice);
                setAllSelected(true);
            }
        } else if (event.key === "Tab") {
            event.preventDefault();
            const focusableElements = drawerRef.current?.querySelectorAll("input, button") || [];
            const currentIndex = Array.from(focusableElements).indexOf(document.activeElement as Element);
            const nextIndex = (currentIndex + 1) % focusableElements.length;
            (focusableElements[nextIndex] as HTMLElement)?.focus();
        }
    };


    useEffect(() => {
        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [focusedRow, routesInvoice, selectedRows]);

    const printInvoice = (rowSelected: Ticket) => {
        if (rowSelected.url_invoice) {
            printJS({ printable: rowSelected.url_invoice, type: "pdf" });
        } else {
            console.error("No se encontró la URL de la factura.");
        }
    };

    const printInvoiceAssignable = (rowSelected: Ticket) => {
        if (rowSelected.url_invoice) {
            printJS({ printable: rowSelected.url_invoice_assignable, type: "pdf" });
        } else {
            console.error("No se encontró la URL de la factura.");
        }
    };

    const onDetails = (rowSelected: Ticket) => {
        setRowSelected(rowSelected)
        setShowDrawer('detail')
    }

    const onDeleteConfirm = async () => {
        if (rowSelected) {
            setLoading(true);
            await deleteTicket(rowSelected.id)
            await getRoutesInvoiceAPI();
            setLoading(false);
            setShowModalConfirm(false);
        }
    }

    const onFilter1 = (option) => {
        setSelectedSupplier(option)
    }

    const optionLabel1 = (option) => {
        return option.name_rz + ' - ' + option.rut
    }

    const printMassive = () => {
        if (selectedRows.length === 0) {
            console.error("No hay facturas seleccionadas.");
            return;
        }

        selectedRows.forEach((row, index) => {
            setTimeout(() => {
                if (row.url_invoice) {
                    printJS({ printable: row.url_invoice, type: "pdf" });
                } else {
                    console.error(`No se encontró la URL de la factura para el ticket ${row.id}`);
                }
            }, index * 1000); // Retraso de 1 segundo entre impresiones
        });
    };

    const printMassivessignable = () => {
        if (selectedRows.length === 0) {
            console.error("No hay facturas seleccionadas.");
            return;
        }

        selectedRows.forEach((row, index) => {
            setTimeout(() => {
                if (row.url_invoice) {
                    printJS({ printable: row.url_invoice_assignable, type: "pdf" });
                } else {
                    console.error(`No se encontró la URL de la factura para el ticket ${row.id}`);
                }
            }, index * 1000); // Retraso de 1 segundo entre impresiones
        });
    };

    const markClosure = async () => {
        await updateRoute(routeSelected.id, { status_closure: 2 })
        await getRoutesInvoiceAPI();
    }

    const markPostponed = async (rowSelected: Ticket) => {
        if (rowSelected?.status_route === "1") {
            await updateStatusRouteTicket(rowSelected?.id, { status_route: "2" })
        } else {
            await updateStatusRouteTicket(rowSelected?.id, { status_route: "1" })
        }
        await getRoutesInvoiceAPI();
    }

    return (
        <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={handleCloseDrawer}
            PaperProps={{ sx: { width: 1370, backgroundColor: "#f9f9f9", boxShadow: 2 } }}
            ref={drawerRef}
        >
            <Box sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Facturas de la Ruta: {routeSelected?.n_route}
                </Typography>
                <Button onClick={markClosure}
                    disabled={routeSelected?.status_closure === "2" ? true : false}
                    sx={{
                        backgroundColor: '#516E80',
                        borderRadius: '8px',
                        color: '#ffffff',
                        height: '32px',
                        padding: '4px 8px',
                        textTransform: 'none',
                        minWidth: '80px',
                        "&:hover": {
                            backgroundColor: "#3A556A"
                        }
                    }}>
                    Marcar cierre
                </Button>

                <Box ref={drawerRef}>
                    <CustomTable
                        columns={columns}
                        rows={routesInvoice || []}
                        onRowClick={() => { }}
                        hasOptions
                        loading={status === ApiStatus.FETCHING}
                        totalRows={n_routeInvoice}
                        dense
                        isSelected={isSelected}
                        selectAll={selectAllRows}
                        allSelected={allSelected}
                        onCheckbox={handleSelectItem}
                        rowsPerPage={perPage}
                        page={page}
                        handleChangePage={handleChangePage}
                        handleChangeRowsPerPage={handleChangeRowsPerPage}
                        focusedRow={focusedRow}
                        setFocusedRow={setFocusedRow}
                        // onButton2={printInvoice}
                        // labelButton2={"Imprimir factura"}
                        // onButton3={printInvoiceAssignable}
                        // labelButton3={"Imprimir cedible"}
                        onButton4={onDetails}
                        labelButton4={"Ver detalle"}

                        onDynamicButton={markPostponed}
                        labelDynamicButton={(row) => row.status_route === "1" ? "Marcar pospuesto" : "Marcar conforme"}
                        disabledDynamicButton={routeSelected?.status_closure === "2"}

                        onSearch={onSearch}

                        onAddFn={printMassive}
                        disabledAdd={selectedRows.length === 0}
                        onAddFnName={'Imprimir facturas'}
                        onAddFn2={printMassivessignable}
                        disabledAdd2={selectedRows.length === 0}
                        onAddFnName2={'Imprimir cedibles'}

                        filter1={selectedSupplier}
                        optionsFilter1={supppliers}
                        optionLabel1={optionLabel1}
                        labelFilter1="Cliente"
                        onFilter1={onFilter1}
                        withCustomPagination
                    />
                </Box>
            </Box>
            {showDrawer === 'detail' && (
                <SaleDetailDrawer
                    open={showDrawer === 'detail'}
                    onClose={() => setShowDrawer('')}
                    ticket={rowSelected}
                    onDeleteConfirm={onDeleteConfirm}
                    disabledEdit={routeSelected?.status_closure === "2" ? true : false}
                    loading={loading}
                />
            )}
        </Drawer>
    );
};
